import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { Analytics } from '@genoa/analytics'
import { ResponseChoice } from '@genoa/domain'
import { AmplitudeFeatureFlag } from '@genoa/experiments'
import { OverwriteCreateResponse } from '@genoa/middle-end'
import { OVERWRITE_AMOUNT_VALIDATION as content } from '@genoa/screen-content'
import { ordinalSuffixFor, toCents, toFormattedDollars } from '@genoa/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { DateTime } from 'luxon'
import * as yup from 'yup'

import { useAuthState } from '../../../../../../contexts'
import { useAutopilotEligibility } from '../../../../../../hooks'
import { useAmplitudeFeatureFlag } from '../../../../../../hooks/use-amplitude-feature-flag'
import { useLazyGetPaymentStatusQuery } from '../../../../../../modules/flexApi'
import { useAnalytics, useLogger } from '../../../../../../providers'
import * as Routes from '../../../../../../routing/constants'
import { FullScreenSpinnerLoading } from '../../../../../components'
import { rentValidator } from '../../../rent-amount/common'
import { BalanceOutOfRange, BTH_BTL } from './BalanceOutOfRange'
import { useConfirmSchedule } from './useConfirmSchedule'
import { useSubmitOverwriteCreate } from './useSubmitOverwriteCreate'
import { useSyncCustomerStatus } from './useSyncCustomerStatus'

export interface UpdatedPaymentAmountState {
  readonly updatedPaymentAmount: number
}

export const BalanceOutOfRangeDetailsContainer = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { uid } = useAuthState()
  const analytics = useAnalytics()
  const [skipMonthModalVisible, setSkipMonthModalVisible] = useState(false)
  const [getPaymentStatus] = useLazyGetPaymentStatusQuery()
  const [isGetPaymentStatusLoading, setIsGetPaymentStatusLoading] = useState(false)
  const { isCustomerStatusSynced, syncCustomerStatus } = useSyncCustomerStatus()
  const loggerV2 = useLogger('BalanceOutOfRangeDetailsContainer')

  const { enabled: janBPRefactorEnabled } = useAmplitudeFeatureFlag(AmplitudeFeatureFlag.PaymentStatusRefactorJanBP)

  const submitOverwriteCreate = useSubmitOverwriteCreate()
  const confirmSchedule = useConfirmSchedule()

  const { isFetchingEligibility, isUserAutopayEnabled } = useAutopilotEligibility()

  useEffect(() => {
    if (isUserAutopayEnabled) {
      syncCustomerStatus()
    }
  }, [isUserAutopayEnabled])

  const { actual_amount, bp_start_date, bp_end_date, estimated_amount, failure_code, payment_config } =
    location.state.embedPayNowEligibility
  const defaultPaymentAmount = actual_amount / 100

  const overwriteCreateSchema = yup.object({ updatedPaymentAmount: rentValidator })
  const overwriteHigherCreateSchema = yup.object({
    updatedPaymentAmount: rentValidator.test('cannot-be-lower', content.LESS_THAN_ACTUAL, (updatedPaymentAmount) =>
      updatedPaymentAmount ? updatedPaymentAmount >= defaultPaymentAmount : false
    ),
  })
  const overwriteLowerCreateSchema = yup.object({
    updatedPaymentAmount: rentValidator.test('cannot-be-higher', content.MORE_THAN_ACTUAL, (updatedPaymentAmount) =>
      updatedPaymentAmount ? updatedPaymentAmount <= defaultPaymentAmount : false
    ),
  })
  const overwriteEqualCreateSchema = yup.object({
    updatedPaymentAmount: rentValidator.test('must-be-equal', content.MUST_EQUAL, (updatedPaymentAmount) =>
      updatedPaymentAmount ? updatedPaymentAmount === defaultPaymentAmount : false
    ),
  })

  const getValidationSchema = (canOverwriteToHigherAmount: boolean, canOverwriteToLowerAmount: boolean) => {
    if (canOverwriteToHigherAmount && canOverwriteToLowerAmount) {
      return overwriteCreateSchema
    } else if (!canOverwriteToHigherAmount && canOverwriteToLowerAmount) {
      return overwriteLowerCreateSchema
    } else if (canOverwriteToHigherAmount && !canOverwriteToLowerAmount) {
      return overwriteHigherCreateSchema
    } else if (!canOverwriteToHigherAmount && !canOverwriteToLowerAmount) {
      return overwriteEqualCreateSchema
    }
    return overwriteEqualCreateSchema
  }

  const {
    control,
    formState: { errors: errorsForm },
    handleSubmit,
  } = useForm<UpdatedPaymentAmountState>({
    mode: 'onChange',
    resolver: yupResolver(
      getValidationSchema(payment_config.can_overwrite_to_higher_amount, payment_config.can_overwrite_to_lower_amount)
    ),
  })

  const onSkipMonth = () => {
    setSkipMonthModalVisible(true)
  }

  const onCloseSkipMonthModal = () => {
    setSkipMonthModalVisible(false)
  }

  const skipMonthConfirmScheduleSuccess = () => {
    analytics.logEvent(Analytics.Events.EMBED_BALANCE_OUT_OF_RANGE_DONT_USE_FLEX_SUCCESS)
    const bpMonth = DateTime.fromFormat(bp_end_date, 'yyyy-MM-dd').monthLong
    const bpDate = DateTime.fromFormat(bp_start_date, 'yyyy-MM-dd')
    const bpStartDate = `${bpDate.monthLong} ${ordinalSuffixFor(bpDate.day)}`
    navigate(Routes.Embed.NOT_COVERED, {
      replace: true,
      state: { bpMonth, bpStartDate },
    })
  }

  const submitSkipMonthConfirmSchedule = (response: OverwriteCreateResponse) => {
    if (response.overwrite_public_id) {
      confirmSchedule.submit(response.overwrite_public_id, skipMonthConfirmScheduleSuccess)
    }
  }

  const onSkipMonthSubmit = () => {
    analytics.logEvent(Analytics.Events.EMBED_BALANCE_OUT_OF_RANGE_DONT_USE_FLEX_CLICKED)
    submitOverwriteCreate.submit(ResponseChoice.ALREADY_PAID, '0', submitSkipMonthConfirmSchedule)
  }

  const handleBalanceTooLowSubmitResponse = async (response: OverwriteCreateResponse, rentPaymentAmount: string) => {
    analytics.logEvent(
      failure_code === BTH_BTL.BALANCE_TOO_HIGH
        ? Analytics.Events.EMBED_BTH_BALANCE_OUT_OF_RANGE_AUTOPAY_ON_SUCCESS
        : Analytics.Events.EMBED_BTL_BALANCE_OUT_OF_RANGE_AUTOPAY_ON_SUCCESS
    )
    if (response.overwrite_public_id) {
      let paymentStatusResponse
      if (janBPRefactorEnabled) {
        try {
          setIsGetPaymentStatusLoading(true)
          paymentStatusResponse = await getPaymentStatus({
            customerId: uid!,
            history: false,
            overrideBillAmount: Number(rentPaymentAmount) * 100,
          })
        } catch (error) {
          const message = error instanceof Error ? error?.message : 'An unexpected error occured'
          loggerV2.error('handleBalanceTooLowSubmitResponse-getPaymentStatus', `error: ${message}`)
        } finally {
          setIsGetPaymentStatusLoading(false)
        }
      }
      navigate(Routes.Embed.BALANCE_OUT_OF_RANGE_PAYMENT_SCHEDULE, {
        replace: true,
        state: {
          processingFeePercentage: response.processing_fee.debit_card_processing_fee_bps,
          payments: response.payments,
          overwritePublicId: response.overwrite_public_id,
          paymentStatus: paymentStatusResponse?.data?.data || [],
        },
      })
    }
  }

  const onSubmitUserInput = handleSubmit((data: UpdatedPaymentAmountState) => {
    const rentPaymentAmount = String(data.updatedPaymentAmount)
    if (isUserAutopayEnabled) {
      analytics.logEvent(
        failure_code === BTH_BTL.BALANCE_TOO_HIGH
          ? Analytics.Events.EMBED_BTH_BALANCE_OUT_OF_RANGE_AUTOPAY_ON_CLICKED
          : Analytics.Events.EMBED_BTL_BALANCE_OUT_OF_RANGE_AUTOPAY_ON_CLICKED
      )
      const responseChoice =
        failure_code === BTH_BTL.BALANCE_TOO_HIGH ? ResponseChoice.ADDITIONAL_CHARGES : ResponseChoice.BALANCE_TOO_LOW
      submitOverwriteCreate.submit(responseChoice, rentPaymentAmount, handleBalanceTooLowSubmitResponse)
    } else if (rentPaymentAmount) {
      analytics.logEvent(
        failure_code === BTH_BTL.BALANCE_TOO_HIGH
          ? Analytics.Events.EMBED_BTH_BALANCE_OUT_OF_RANGE_EPISODIC_CLICKED
          : Analytics.Events.EMBED_BTL_BALANCE_OUT_OF_RANGE_EPISODIC_CLICKED
      )
      const updatedPaymentAmount = toCents(rentPaymentAmount)
      navigate(Routes.Embed.SIMPLIFIED_EPISODIC, { state: { updatedPaymentAmount } })
    }
  })

  const isSkipMonthSubmissionLoading =
    skipMonthModalVisible && (submitOverwriteCreate.isSubmitting || confirmSchedule.isConfirming)

  const isSubmitLoading = !skipMonthModalVisible && (submitOverwriteCreate.isSubmitting || isGetPaymentStatusLoading)

  if (isFetchingEligibility || (isUserAutopayEnabled && !isCustomerStatusSynced)) {
    return <FullScreenSpinnerLoading />
  }

  return (
    <BalanceOutOfRange
      actualAmount={toFormattedDollars(actual_amount)}
      control={control}
      defaultPaymentAmount={defaultPaymentAmount}
      estimateAmount={toFormattedDollars(estimated_amount)}
      failureCode={failure_code}
      formErrors={errorsForm}
      handleUpdatePaymentPress={onSubmitUserInput}
      isUserAutopayEnabled={isUserAutopayEnabled}
      isSkipMonthSubmissionLoading={isSkipMonthSubmissionLoading}
      isSubmitLoading={isSubmitLoading}
      onCloseSkipMonthModal={onCloseSkipMonthModal}
      onSkipMonth={onSkipMonth}
      onSkipMonthSubmit={onSkipMonthSubmit}
      skipMonthModalVisible={skipMonthModalVisible}
    />
  )
}
